<script lang="ts" setup>
	const { $gsap } = useNuxtApp()

	const service = ref([
		{
			key: 'rocket',
			title: {
				icon: {
					name: 'rocket'
				},
				text: 'Startups'
			},
			list: [
				'We understand the unique challenges faced by early-stage startups in Nigeria.',
				'We offer budget-friendly insurance plans with flexible payment options specifically designed for your lean team and growth aspirations.',
				'Our platform is tech-friendly, allowing you to manage your insurance needs seamlessly and holistically.'
			]
		},
		{
			key: 'shopping-store',
			title: {
				icon: {
					name: 'shopping-store'
				},
				text: 'MSMEs'
			},
			list: [
				'MyCoverGenius caters to Micro, Small, and Medium Enterprises across Nigeria.',
				'We provide affordable coverage solutions that cater to your specific business needs and staff size.',
				'Our platform is user-friendly and caters to both tech-savvy and non-tech-savvy employees.'
			]
		}
		// {
		// 	key: 'building-city',
		// 	title: {
		// 		icon: {
		// 			name: 'building-city'
		// 		},
		// 		text: 'Large Corporations'
		// 	},
		// 	list: [
		// 		'For large corporations, we offer comprehensive insurance packages that cover extensive operations and a large workforce.',
		// 		'Our tailored solutions ensure that all aspects of your business are protected, from assets to employee benefits.',
		// 		'We provide dedicated account management to streamline your insurance processes and meet your specific corporate needs.'
		// 	]
		// }
	])
	const activeKey = ref('rocket')
	const currentIndex = ref(0)
	const activeItem = computed(() => service.value.find((s, index) => index === currentIndex.value))
	const intervalId = ref<ReturnType<typeof setInterval>>()

	const startAnimations = (index = 0) => {
		let matchMedia = $gsap.matchMedia()
		currentIndex.value = index
		matchMedia.add('(min-width: 800px)', () => {
			$gsap.utils.toArray('.service').forEach((el: any, i: number) => {
				if (el) {
					const text = el.querySelector('.sample_text')
					const svg = el.querySelector('svg')
					const inner = el.querySelector('.loader')
					const timeline = $gsap.timeline({ defaults: { duration: 0.7 } })
					if (index === i) {
						timeline
							.to(text, { x: 40, ease: 'expo.in' })
							.fromTo(svg, { opacity: 0 }, { opacity: 1 }, '<0.4')
							.to(inner, {
								strokeDasharray: '100 100',
								strokeDashoffset: '0',
								duration: 8,
								ease: 'power4.inOut'
							})
					} else {
						timeline
							.to(inner, { strokeDasharray: '0 100', strokeDashoffset: '0' })
							.to(svg, { opacity: 0 }, '<')
							.to(text, { x: 0, ease: 'expo.out' }, '<0.3')
					}
				}
			})
		})
	}
	const startInterval = () => {
		currentIndex.value += 1
		if (currentIndex.value >= service.value.length) currentIndex.value = 0
		startAnimations(currentIndex.value)
	}

	onMounted(() => {
		startAnimations()

		intervalId.value = setInterval(startInterval, 10000)
	})
	onUnmounted(() => {
		if (intervalId.value) {
			clearInterval(intervalId.value)
		}
	})
</script>

<template>
	<div class="flexed justify-center flex-col">
		<ui-title-header
			title="Who Do We Serve?"
			:ui="{ size: 'md:text-[3.25em] text-[2em] leading-[38.4px] md:leading-[62.4px]' }"
		/>

		<ui-pad class="w-full mt-10">
			<div class="flex items-stretch flex-wrap gap-10 w-full">
				<div class="md:min-h-[480px] w-full md:flex-1 max-w-[430px] md:border md:p-[70px_39px] rounded-lg">
					<div
						class="self-stretch md:flex-col justify-center md:justify-start items-start gap-3 overflow-x-auto invisible-scrollbar md:gap-8 inline-flex w-full"
					>
						<button
							v-for="(item, index) in service"
							:key="`service-${item.key}`"
							type="button"
							class="service relative md:py-8 p-[13px_14px] gap-x-5 flex-none md:w-full custom-transition-2 truncate px-3.5 border py-[13px] md:rounded rounded-[40px] justify-start items-center inline-flex"
							:class="[index === currentIndex ? 'bg-main text-white' : 'border-main text-main']"
							@click="startAnimations(index)"
						>
							<svg
								class="absolute top-8 left-4 hidden md:block"
								width="24"
								height="24"
								viewBox="0 0 120 120"
							>
								<circle cx="60" cy="60" r="54" fill="none" stroke="#fbf7fd80" stroke-width="7.8" />
								<circle
									class="loader"
									cx="60"
									cy="60"
									r="54"
									fill="none"
									stroke="white"
									stroke-width="5.8"
									pathLength="100"
								/>
							</svg>

							<div class="justify-start items-center gap-5 inline-flex sample_text">
								<div class="md:text-2xl text-sm font-semibold md:leading-7">
									{{ item.title.text }}
								</div>
							</div>
						</button>
					</div>
				</div>

				<div class="min-h-[480px] border md:flex-1 rounded-lg relative">
					<div class="flex items-center md:my-10 mt-10 px-5 gap-3">
						<tools-icon
							:dir="activeItem?.title.icon?.dir ?? 'random'"
							:icon="activeItem?.title.icon.name ?? ''"
						/>
						<div class="text-gray-700 md:text-[28px] font-semibold md:leading-[33.60px]">
							{{ activeItem?.title.text }}
						</div>
					</div>

					<ul class="sm:px-10 px-5">
						<li
							v-for="(item, index) in activeItem?.list || []"
							:key="'service list-' + index"
							class="flex items-center gap-5 py-5"
						>
							<div class="w-4 h-4 bg-slate-500 rounded-full relative flex-none">
								<div class="w-2 h-2 left-[4px] top-[4px] absolute bg-yellow-500 rounded-full"></div>
							</div>

							<div class="max-w-[574px] text-gray-700 md:text-lg font-normal md:leading-[25.20px]">
								{{ item }}
							</div>
						</li>
					</ul>
				</div>
			</div>
		</ui-pad>
	</div>
</template>

<style scoped>
	.circle-bg {
		fill: none;
		stroke: #fbf7fd80;
		stroke-width: 3.8;
	}

	.circle {
		--width: 100;
		fill: none;
		stroke-width: 2.8;
		stroke-linecap: round;
		transition: 1s ease-out;
	}

	.circular-chart .circle {
		stroke: #fff;
	}
</style>
